import {TradeCounterOffer, TradeCreationData, TradeError, TradeResponse} from '@colonist/game-mechanic-validators'
import {CardEnum, PlayerColor, TradeId} from '@colonist/game-states'
import {StateCreator} from 'zustand'
import {GameStoreType} from '../gameStore'

export interface GameTradeSlice {
    gameTrade: {
        wantedCards: CardEnum[]
        setWantedCards: (wantedCards: CardEnum[]) => void
        isShowTradePanel: boolean
        setIsShowTradePanel: (isShowTradePanel: boolean) => void
        isBankTradeMode: boolean
        setIsBankTradeMode: (isBankTradeMode: boolean) => void
        tradeError: TradeError | undefined
        setTradeError: (tradeError: TradeError | undefined) => void
        _tradeErrorTimeoutRef: NodeJS.Timeout | null
        counterOffer: TradeCounterOffer | undefined
        setCounterOffer: (counterOffer: TradeCounterOffer | undefined) => void
        onClickCreateTrade: (tradeCreationData: TradeCreationData) => void
        onClickToggleEmbargoAgainstPlayer: (color: PlayerColor) => void
    }
    gameTradeOffers: {
        isTradeOffersVisible: boolean
        setIsTradeOffersVisible: (isTradeOffersVisible: boolean) => void
        sendUpdateTradeResponse: (tradeResponse: TradeResponse) => void
        onClickExecuteTrade: (tradeId: TradeId, playerColor: PlayerColor) => void
    }
}

export interface GameTradeSliceProps {
    sendUpdateTradeResponse: (tradeResponse: TradeResponse) => void
    onClickExecuteTrade: (tradeId: TradeId, playerColor: PlayerColor) => void
    onClickCreateTrade: (tradeCreationData: TradeCreationData) => void
    onClickToggleEmbargoAgainstPlayer: (color: PlayerColor) => void
    sendCancelAction: () => void
}

export const createGameTradeSlice = ({
    sendUpdateTradeResponse,
    onClickExecuteTrade,
    onClickCreateTrade,
    onClickToggleEmbargoAgainstPlayer,
    sendCancelAction,
}: GameTradeSliceProps): StateCreator<GameStoreType, [], [], GameTradeSlice> => (set, get) => ({
    gameTrade: {
        wantedCards: [],
        setWantedCards: (wantedCards: CardEnum[]) => set({
            gameTrade: {
                ...get().gameTrade,
                wantedCards: wantedCards,
            },
        }),
        isShowTradePanel: false,
        setIsShowTradePanel: (isShowTradePanel: boolean) => {
            get().gameBeginnerHint.setBeginnerHintType(undefined)
            if(isShowTradePanel) {
                sendCancelAction()
                get().actionBox.hideActionBox()
            }

            const playOrder = get().gameDetails.playOrder
            const is1v1 = playOrder.length === 2
            const isMatchmakingGame = get().gameDetails.matchmakingMatchType != undefined
            const hasCounterOffer = get().gameTrade.counterOffer != undefined
            const shouldBankTradeModeBeToggledByDefault = is1v1 && isMatchmakingGame && !hasCounterOffer
            if(shouldBankTradeModeBeToggledByDefault) {
                set({
                    gameTrade: {
                        ...get().gameTrade,
                        isBankTradeMode: true,
                    },
                })
            }

            set({
                gameTrade: {
                    ...get().gameTrade,
                    isShowTradePanel: isShowTradePanel,
                    counterOffer: !isShowTradePanel ? undefined : get().gameTrade.counterOffer,
                },
            })
        },
        isBankTradeMode: false,
        setIsBankTradeMode: (isBankTradeMode: boolean) => set({
            gameTrade: {
                ...get().gameTrade,
                isBankTradeMode: isBankTradeMode,
            },
        }),
        tradeError: undefined,
        setTradeError: (tradeError: TradeError | undefined) => {
            const state = get()

            if(state.gameTrade._tradeErrorTimeoutRef != null) {
                clearTimeout(state.gameTrade._tradeErrorTimeoutRef)
                set({
                    gameTrade: {
                        ...get().gameTrade,
                        _tradeErrorTimeoutRef: null,
                    },
                })
            }

            set({
                gameTrade: {
                    ...get().gameTrade,
                    tradeError: tradeError,
                },
            })

            if(tradeError) {
                const timeoutId = setTimeout(() => {
                    set({
                        gameTrade: {
                            ...get().gameTrade,
                            tradeError: undefined,
                            _tradeErrorTimeoutRef: null,
                        },
                    })
                }, 1000)

                set({
                    gameTrade: {
                        ...get().gameTrade,
                        _tradeErrorTimeoutRef: timeoutId,
                    },
                })
            }
        },
        _tradeErrorTimeoutRef: null,
        counterOffer: undefined,
        setCounterOffer: (counterOffer: TradeCounterOffer | undefined) => set({
            gameTrade: {
                ...get().gameTrade,
                counterOffer: counterOffer,
                isBankTradeMode: false,
            },
        }),
        onClickCreateTrade: onClickCreateTrade,
        onClickToggleEmbargoAgainstPlayer: onClickToggleEmbargoAgainstPlayer,
    },
    gameTradeOffers: {
        sendUpdateTradeResponse: sendUpdateTradeResponse,
        onClickExecuteTrade: onClickExecuteTrade,
        isTradeOffersVisible: true,
        setIsTradeOffersVisible: (isTradeOffersVisible: boolean) => set({
            gameTradeOffers: {
                ...get().gameTradeOffers,
                isTradeOffersVisible: isTradeOffersVisible,
            },
        }),
    },
})
